import fetchIntercept from "fetch-intercept";
import Resource from '../core/serverresource';
import type {EntraIdApiConfiguration} from './entraid/EntraIdConfiguration';

export type AccessTokenProvider = () => Promise<string>;
export type TokenFailureHandler = (error: unknown) => void;
export type InterceptorUnregister = () => void;

/**
 * Returns true only for the configured Smart SES API origin.
 * This allow-list is evaluated before token acquisition so nested MSAL authority/token fetches
 * bypass the interceptor instead of recursively requesting another access token.
 */
const isWebApiRequest = (request: RequestInfo | URL, webApiUrl: string): boolean => {
    try {
        // Resolve every supported fetch input against the page URL so relative API URLs work too.
        let requestTarget: string;
        if (typeof request === 'string') {
            requestTarget = request;
        } else if (request instanceof URL) {
            requestTarget = request.href;
        } else {
            requestTarget = request.url;
        }
        const requestUrl = new URL(requestTarget, window.location.href);
        const apiUrl = new URL(webApiUrl);

        return requestUrl.origin === apiUrl.origin;
    } catch {
        // Invalid request/configuration URLs fail closed: do not acquire or disclose credentials.
        return false;
    }
};

/**
 * Registers a fetch interceptor that adds the Authorization header with
 * the provided access token to all outgoing requests.
 *
 * @param accessToken - The access token to be included in the Authorization header.
 */
export function registerInterceptor(accessToken: string) {
    fetchIntercept.clear();
    fetchIntercept.register({
        request: (url, config) => {
            if (!config) {
                config = {};
            }
            if (!config.headers) {
                config.headers = {};
            }
            config.headers['Authorization'] = 'Bearer ' + accessToken;
            config.headers["web-api-key"] = Resource.getWebApiKey();
            return [url, config];
        }
    });
}

/**
 * Registers a fetch interceptor that retrieves an access token from the provided token provider
 * and adds it to configured Smart SES API requests. Other traffic, including MSAL requests,
 * bypasses token acquisition.
 *
 * @param getAccessToken - Retrieves a current Entra ID access token.
 * @param onTokenFailure - Reports a failed token retrieval to the caller.
 * @param apiConfiguration - API URL and key validated before MSAL construction.
 * @returns A callback that removes only this interceptor registration.
 */
export function registerMsalInterceptor(
    getAccessToken: AccessTokenProvider,
    onTokenFailure: TokenFailureHandler,
    apiConfiguration: EntraIdApiConfiguration,
): InterceptorUnregister {
    return fetchIntercept.register({
        request: async function (url, config) {
            if (!isWebApiRequest(url, apiConfiguration.webApiUrl)) {
                return [url, config];
            }

            let token: string;
            try {
                token = await getAccessToken();
            } catch (error) {
                onTokenFailure(error);
                throw error;
            }
            if (!config) {
                config = {};
            }
            if (!config.headers) {
                config.headers = {};
            }
            config.headers['Authorization'] = 'Bearer ' + token;
            config.headers["web-api-key"] = apiConfiguration.apiKey;
            return [url, config];
        }
    });
}